import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  History,
  Plus,
  Check,
  Trash2,
  Loader2,
  FileText,
  ChevronDown,
  RefreshCw,
  Save,
  Upload,
  GitBranch,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { API_URL } from '../../config';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function VersionManager({ 
  organizationId, 
  spaceId, 
  spaceName, 
  selectedVersionId,
  onVersionSelect,
  onVersionChange,
  onSave,
  saving,
  hasUnsavedChanges
}) {
  const { token } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleteVersion, setDeleteVersion] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '',
    source_version_id: '' // Empty string means "Live Content"
  });

  const activeVersion = versions.find(v => v.is_active);
  const selectedVersion = versions.find(v => v.id === selectedVersionId);
  const isLiveSelected = !selectedVersionId;
  const isLivePublished = !activeVersion; // Live is "published" when no version is active

  const fetchVersions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/versions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVersions(response.data);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId, spaceId, token]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Set default source_version_id when opening create dialog
  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      source_version_id: selectedVersionId || '__live__' // Default to currently selected version
    });
    setShowCreate(true);
  };

  const handleCreateVersion = async () => {
    setCreating(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        source_version_id: formData.source_version_id === '__live__' ? null : formData.source_version_id
      };
      
      const response = await axios.post(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/versions`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setVersions(prev => [response.data, ...prev]);
      setShowCreate(false);
      setFormData({ name: '', description: '', source_version_id: '' });
      toast.success('Version created successfully!');
      
      // Auto-select the new version
      onVersionSelect?.(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create version');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectVersion = async (versionId) => {
    if (!versionId) {
      // Switch to Live
      onVersionSelect?.(null);
      return;
    }
    onVersionSelect?.(versionId);
  };

  // Publish selected version OR publish live (deactivate all versions)
  const handlePublish = async () => {
    setPublishing(true);
    try {
      if (!selectedVersionId) {
        // Publishing Live = deactivate all versions
        await axios.put(
          `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/versions/deactivate`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local state - all versions inactive
        setVersions(prev => prev.map(v => ({ ...v, is_active: false })));
        toast.success('Live content published! Visitors will now see live content.');
      } else {
        // Publishing a specific version
        await axios.put(
          `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/versions/${selectedVersionId}/publish`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local state - mark this version as active, others as not active
        setVersions(prev => prev.map(v => ({
          ...v,
          is_active: v.id === selectedVersionId,
          is_published: v.id === selectedVersionId ? true : v.is_published
        })));
        
        toast.success('Version published! Visitors will now see this version.');
      }
      onVersionChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteVersion = async () => {
    if (!deleteVersion) return;
    
    try {
      await axios.delete(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/versions/${deleteVersion.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVersions(prev => prev.filter(v => v.id !== deleteVersion.id));
      toast.success('Version deleted');
      
      // If deleted version was selected, switch to live
      if (selectedVersionId === deleteVersion.id) {
        onVersionSelect?.(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete version');
    } finally {
      setDeleteVersion(null);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="version-manager">
      {/* Version Selector Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2 min-w-[140px] justify-between"
            data-testid="version-selector-trigger"
          >
            <div className="flex items-center gap-2">
              <History className="w-4 h-4" />
              {isLiveSelected ? (
                <span className={cn(
                  "font-medium",
                  isLivePublished ? "text-emerald-600 dark:text-emerald-400" : ""
                )}>Live</span>
              ) : selectedVersion ? (
                <span className="truncate max-w-[100px]">{selectedVersion.name}</span>
              ) : (
                <span className="text-slate-500">Select...</span>
              )}
            </div>
            <ChevronDown className="w-4 h-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72" data-testid="version-dropdown">
          <div className="px-2 py-1.5 text-sm font-semibold text-slate-900 dark:text-white">
            Select Version
          </div>
          <DropdownMenuSeparator />
          
          {/* Live/Current option */}
          <DropdownMenuItem
            onClick={() => handleSelectVersion(null)}
            className={cn("gap-2 cursor-pointer", isLiveSelected && "bg-blue-50 dark:bg-blue-900/20")}
          >
            <RefreshCw className={cn("w-4 h-4", isLivePublished ? "text-emerald-600" : "text-slate-400")} />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">Live (Current)</span>
                {isLivePublished && (
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                    Published
                  </Badge>
                )}
              </div>
              <div className="text-xs text-slate-500">Edit latest content</div>
            </div>
            {isLiveSelected && <Check className="w-4 h-4 text-blue-600" />}
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {loading ? (
            <div className="py-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            </div>
          ) : versions.length === 0 ? (
            <div className="py-4 text-center text-sm text-slate-500">
              No versions saved yet
            </div>
          ) : (
            <ScrollArea className="max-h-64">
              {versions.map((version) => (
                <DropdownMenuItem
                  key={version.id}
                  onClick={() => handleSelectVersion(version.id)}
                  className={cn(
                    "gap-2 py-2 cursor-pointer group", 
                    selectedVersionId === version.id && "bg-blue-50 dark:bg-blue-900/20"
                  )}
                >
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {version.name}
                      {version.is_active && (
                        <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                          Published
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>{version.document_count} docs</span>
                      <span>•</span>
                      <span>{formatDate(version.created_at)}</span>
                    </div>
                  </div>
                  {selectedVersionId === version.id && <Check className="w-4 h-4 text-blue-600" />}
                  
                  {/* Delete button on hover */}
                  {!version.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-red-100 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteVersion(version);
                      }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          )}
          
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleOpenCreate} className="gap-2 cursor-pointer text-blue-600">
            <Plus className="w-4 h-4" />
            Create New Version
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Split Save Button */}
      <div className="flex items-center">
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving || !hasUnsavedChanges}
          className="rounded-r-none bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="save-button"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-1" />
          )}
          Save
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="rounded-l-none border-l border-blue-700 bg-blue-600 hover:bg-blue-700 text-white px-2"
              data-testid="save-dropdown-trigger"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleOpenCreate} className="gap-2 cursor-pointer">
              <GitBranch className="w-4 h-4" />
              Save as New Version
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Publish Button */}
      <Button
        size="sm"
        onClick={handlePublish}
        disabled={publishing || (isLiveSelected && isLivePublished) || (selectedVersion?.is_active)}
        className={cn(
          "gap-2",
          (isLiveSelected && isLivePublished) || selectedVersion?.is_active
            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400"
            : "bg-emerald-600 hover:bg-emerald-700 text-white"
        )}
        data-testid="publish-button"
      >
        {publishing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (isLiveSelected && isLivePublished) || selectedVersion?.is_active ? (
          <>
            <Check className="w-4 h-4" />
            Published
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Publish
          </>
        )}
      </Button>

      {/* Create Version Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-version-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-blue-600" />
              Create New Version
            </DialogTitle>
            <DialogDescription>
              Save a snapshot of the current content as a new version. You can branch from any existing version.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="version-name">Version Name *</Label>
              <Input
                id="version-name"
                placeholder="e.g., v1.0, Launch Ready, Q1 Release"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="version-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="version-description">Description</Label>
              <Textarea
                id="version-description"
                placeholder="What changes are included in this version?"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[80px]"
                data-testid="version-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="source-version">Based on</Label>
              <Select
                value={formData.source_version_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, source_version_id: value }))}
              >
                <SelectTrigger id="source-version" data-testid="source-version-select">
                  <SelectValue placeholder="Select source version" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__live__">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-emerald-600" />
                      <span className="text-emerald-700 dark:text-emerald-400">Live Content (Current)</span>
                    </div>
                  </SelectItem>
                  {versions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span>{v.name}</span>
                        {v.is_active && (
                          <Badge className="ml-1 text-xs" variant="secondary">Published</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                The new version will be created from the content of the selected source.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateVersion}
              disabled={!formData.name.trim() || creating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-version-submit"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Version
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteVersion} onOpenChange={() => setDeleteVersion(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Version</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteVersion?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVersion}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
